import { _ as _class_call_check } from "@swc/helpers/_/_class_call_check";
import { _ as _create_class } from "@swc/helpers/_/_create_class";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { PageInitialized } from "@faire/retailer-visitor-shared/events";
import { setTimeoutIfPossible } from "@faire/retailer-visitor-shared/lib/async-functions/setTimeoutIfPossible";
import { getReleaseSha } from "@faire/retailer-visitor-shared/serialized-data/getReleaseSha";
import { getReleaseVersion } from "@faire/retailer-visitor-shared/serialized-data/getReleaseVersion";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { pageLoadDurations, PageLoadEvent } from "@faire/retailer-visitor-shared/services/performance/const";
import { getCurrentUrl, getEventStartTime, waitForFrameDelayBelowThreshold } from "@faire/retailer-visitor-shared/services/performance/lib";
import { emitDatadogTiming, getDD_RUM } from "@faire/web--source/common/datadog/rum";
import { getDocumentOrThrow } from "@faire/web--source/common/globals/getDocument";
import { getWindow, getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { HistoryTiming } from "@faire/web--source/common/performance/HistoryTiming";
import { singletonGetter } from "@faire/web--source/common/singletons/getter";
import { enumValues, protoEntries } from "@faire/web--source/common/typescriptUtils";
import postPerformanceReport from "@faire/web-api--source/api/performance/post";
export var PerformanceMarker = /*#__PURE__*/ function() {
    "use strict";
    function PerformanceMarker() {
        var _this = this;
        _class_call_check(this, PerformanceMarker);
        this.recordedEvents = new Set();
        this.reportedToDatadog = new Set();
        this.initialUrl = getCurrentUrl();
        this.logPageLoadEvent = require("debug")("IF:PERF");
        // unused-class-members-ignore-next
        this.init = function() {
            PageInitialized.subscribe(_this.onPageInitialized);
            getDocumentOrThrow().addEventListener("readystatechange", _this.postReport);
        };
        this.mark = function(event) {
            if (_this.recordedEvents.has(event) || !HistoryTiming.get().isInitialLoad) {
                return;
            }
            if (!performance || !performance.mark) {
                return;
            }
            var entry = performance.mark(event);
            var _entry_startTime;
            var timing = (_entry_startTime = entry === null || entry === void 0 ? void 0 : entry.startTime) !== null && _entry_startTime !== void 0 ? _entry_startTime : performance.now();
            _this.logPageLoadEvent(event, timing);
            _this.emitToDatadog(event, timing);
            _this.recordedEvents.add(event);
        };
        /**
         * Emits any of the marks that will have occurred before DD_RUM bootstrapped to datadog retroactively.
         */ this.forwardMarksToDatadog = function() {
            var _getDD_RUM;
            if (!((_getDD_RUM = getDD_RUM()) === null || _getDD_RUM === void 0 ? void 0 : _getDD_RUM.addTiming)) {
                return;
            }
            var marks = _this.performanceMarks;
            var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
            try {
                // Send any unreported marks ( Some marks are recorded before DD_RUM loads ).
                for(var _iterator = marks.entries()[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                    var _step_value = _sliced_to_array(_step.value, 2), mark = _step_value[0], timing = _step_value[1];
                    _this.emitToDatadog(mark, timing);
                }
            } catch (err) {
                _didIteratorError = true;
                _iteratorError = err;
            } finally{
                try {
                    if (!_iteratorNormalCompletion && _iterator.return != null) {
                        _iterator.return();
                    }
                } finally{
                    if (_didIteratorError) {
                        throw _iteratorError;
                    }
                }
            }
            var _iteratorNormalCompletion1 = true, _didIteratorError1 = false, _iteratorError1 = undefined;
            try {
                // Send any unreported durations
                for(var _iterator1 = protoEntries(pageLoadDurations)[Symbol.iterator](), _step1; !(_iteratorNormalCompletion1 = (_step1 = _iterator1.next()).done); _iteratorNormalCompletion1 = true){
                    var _step_value1 = _sliced_to_array(_step1.value, 2), durationName = _step_value1[0], _step_value_ = _sliced_to_array(_step_value1[1], 2), startEvent = _step_value_[0], endEvent = _step_value_[1];
                    if (!marks.has(startEvent) || !marks.has(endEvent)) {
                        continue;
                    }
                    var start = marks.get(startEvent);
                    var end = marks.get(endEvent);
                    _this.emitToDatadog(durationName, end - start);
                }
            } catch (err) {
                _didIteratorError1 = true;
                _iteratorError1 = err;
            } finally{
                try {
                    if (!_iteratorNormalCompletion1 && _iterator1.return != null) {
                        _iterator1.return();
                    }
                } finally{
                    if (_didIteratorError1) {
                        throw _iteratorError1;
                    }
                }
            }
        };
        this.emitToDatadog = function(mark, timing) {
            if (_this.reportedToDatadog.has(mark)) {
                return;
            }
            emitDatadogTiming(mark, timing);
            _this.reportedToDatadog.add(mark);
        };
        this.getDatadogContext = function() {
            var _getDD_RUM_getRumGlobalContext, _getDD_RUM;
            var _getDD_RUM_getRumGlobalContext1;
            return (_getDD_RUM_getRumGlobalContext1 = (_getDD_RUM = getDD_RUM()) === null || _getDD_RUM === void 0 ? void 0 : (_getDD_RUM_getRumGlobalContext = _getDD_RUM.getRumGlobalContext) === null || _getDD_RUM_getRumGlobalContext === void 0 ? void 0 : _getDD_RUM_getRumGlobalContext.call(_getDD_RUM)) !== null && _getDD_RUM_getRumGlobalContext1 !== void 0 ? _getDD_RUM_getRumGlobalContext1 : {};
        };
        this.onPageInitialized = function() {
            // We assume that the main thread is no longer saturated
            // if it takes less than 250ms to schedule the next frame.
            waitForFrameDelayBelowThreshold(250, function() {
                return _this.mark(PageLoadEvent.pageInteractive);
            });
        };
        this.postReport = function() {
            // only send the data once document is loaded
            if (getDocumentOrThrow().readyState === "complete") {
                setTimeoutIfPossible(function() {
                    _this.forwardMarksToDatadog();
                    postPerformanceReport(_object_spread({}, Object.fromEntries(_this.performanceMarks), _this.initialLoadTiming, _this.metadata));
                });
            }
        };
    }
    _create_class(PerformanceMarker, [
        {
            key: "performanceMarks",
            get: function get() {
                var marks = new Map();
                var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
                try {
                    for(var _iterator = enumValues(PageLoadEvent)[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                        var event = _step.value;
                        var markTime = getEventStartTime(event);
                        if (markTime !== undefined) {
                            marks.set(event, markTime);
                        }
                    }
                } catch (err) {
                    _didIteratorError = true;
                    _iteratorError = err;
                } finally{
                    try {
                        if (!_iteratorNormalCompletion && _iterator.return != null) {
                            _iterator.return();
                        }
                    } finally{
                        if (_didIteratorError) {
                            throw _iteratorError;
                        }
                    }
                }
                return marks;
            }
        },
        {
            key: "initialLoadTiming",
            get: function get() {
                var connectStart = performance.timing.connectStart;
                return {
                    domLoading: performance.timing.domLoading - connectStart,
                    domComplete: performance.timing.domComplete - connectStart
                };
            }
        },
        {
            key: "metadata",
            get: function get() {
                var _getWindow, _getWindow1;
                var retailer = getRetailer();
                var _retailer_token;
                return {
                    reportUrl: getCurrentUrl(),
                    initialUrl: this.initialUrl,
                    releaseSha: getReleaseSha(),
                    releaseVersion: getReleaseVersion(),
                    retailerToken: (_retailer_token = retailer === null || retailer === void 0 ? void 0 : retailer.token) !== null && _retailer_token !== void 0 ? _retailer_token : "",
                    type: "initial-page-load",
                    viewportWidth: getWindowOrThrow().innerWidth,
                    viewportHeight: getWindowOrThrow().innerHeight,
                    userAgent: (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.navigator.userAgent,
                    devicePixelRatio: (_getWindow1 = getWindow()) === null || _getWindow1 === void 0 ? void 0 : _getWindow1.devicePixelRatio,
                    ddRumContext: this.getDatadogContext()
                };
            }
        }
    ]);
    return PerformanceMarker;
}();
PerformanceMarker.get = singletonGetter(PerformanceMarker);
